import { useMemo } from 'react';
import type { FC } from 'react';

import Grid from '../Grid';
import type { GridContainerProps } from '../Grid';

import { StyledTickButton, StyledTickItem, StyledTicksContainer } from './Slider.styles';
import type SliderProps from './Slider.types';
import { calculateTicksRegions } from './utils';

interface SliderTicksProps {
  ticks: NonNullable<SliderProps['ticks']>;
  min: number;
  max: number;
  step: number;
  orientation: NonNullable<SliderProps['orientation']>;
  onClick: (value: number) => void;
}

const SliderTicks: FC<SliderTicksProps> = ({
  ticks,
  min,
  max,
  step,
  orientation,
  onClick
}: SliderTicksProps) => {
  const cellsCount = (max - min) / step + 1;

  const ticksDefinitions = useMemo(
    () => calculateTicksRegions(ticks, min, max, step, orientation === 'horizontal'),
    [ticks, min, max, orientation]
  );

  let repeatCells: string;
  let gridContainerConfig: GridContainerProps;
  if (orientation === 'horizontal') {
    repeatCells = cellsCount > 2 ? `repeat(${cellsCount - 2}, minmax(0, 1fr))` : '';
    gridContainerConfig = {
      cols: `minmax(0, 0.5fr) ${repeatCells} minmax(0, 0.5fr)`
    };
  } else {
    repeatCells = `repeat(${cellsCount}, 1em)`;
    gridContainerConfig = { rows: repeatCells, alignContent: 'between' };
  }

  return (
    <Grid container={gridContainerConfig} as={StyledTicksContainer}>
      {ticksDefinitions.map(tick => {
        return (
          <StyledTickItem
            item={
              orientation === 'horizontal'
                ? {
                    colStartEnd: `${tick.start} / ${tick.end}`
                  }
                : {
                    rowStartEnd: `${-tick.start} / ${-tick.end}`
                  }
            }
            key={tick.label ?? tick.value}
            orientation={orientation}
            lowerBoundaryItem={tick.value === min}
            upperBoundaryItem={tick.value === max}
          >
            <StyledTickButton
              tabIndex={-1}
              role='button'
              aria-hidden
              onClick={() => onClick(tick.value)}
            >
              {tick.label}
            </StyledTickButton>
          </StyledTickItem>
        );
      })}
    </Grid>
  );
};

export default SliderTicks;
